/**
 * ============================================================================
 * StartRequalificationDialog — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: a validation lead picks up a PENDING requalification task. We
 *          collect:
 *            - validation lead user id / name
 *            - signature bundle id
 *          The server records started_at_utc and the lead.
 *
 * A11Y: focus moves to the lead field; Escape closes; submit is disabled
 *  while the mutation is in flight.
 * ============================================================================ */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './RequalificationDashboard.module.css';
import {
  useStartRequalMutation,
  type StartRequalInput,
} from './useStartRequalMutation';
import type { RequalScheduleRow } from './useRequalificationScheduleQuery';

export interface StartRequalificationDialogProps {
  row: RequalScheduleRow;
  onClose: () => void;
  onStarted: () => void;
}

export function StartRequalificationDialog({
  row,
  onClose,
  onStarted,
}: StartRequalificationDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const firstRef = useRef<HTMLInputElement | null>(null);

  const startM = useStartRequalMutation();
  const [lead, setLead] = useState('');
  const [sig, setSig] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    firstRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setError(null);
    if (!lead.trim()) {
      setError(
        t('equipment.requalDashboard.startDialog.errors.leadRequired', {
          defaultValue: 'Validation lead user id is required.',
        }),
      );
      return;
    }
    if (!sig.trim()) {
      setError(
        t('equipment.requalDashboard.startDialog.errors.sigRequired', {
          defaultValue: 'A signature bundle id is required.',
        }),
      );
      return;
    }
    const input: StartRequalInput = {
      requalificationId: row.requalification_id,
      validation_lead: lead.trim(),
      signature_bundle_id: sig.trim(),
    };
    try {
      await startM.mutateAsync(input);
      onStarted();
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : t('equipment.requalDashboard.startDialog.errors.serverFailed', {
              defaultValue: 'Server rejected the start.',
            }),
      );
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('equipment.requalDashboard.startDialog.title', {
              defaultValue: 'Start requalification',
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {t('equipment.requalDashboard.startDialog.subtitle', {
              defaultValue:
                'Pick up the requalification task for {{tag}} ({{name}}). The server records you as the validation lead and stamps started_at_utc.',
              tag: row.equipment_tag,
              name: row.equipment_name,
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          <div className={dialogStyles.field}>
            <label htmlFor="rq-start-lead" className={dialogStyles.label}>
              {t('equipment.requalDashboard.startDialog.lead', {
                defaultValue: 'Validation lead',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <input
              id="rq-start-lead"
              ref={firstRef}
              className={dialogStyles.input}
              value={lead}
              onChange={(e) => setLead(e.target.value)}
              placeholder="val-lead-1"
              required
            />
            <p className={styles.hint}>
              {t('equipment.requalDashboard.startDialog.leadHint', {
                defaultValue:
                  'User id of the validation lead picking up the task.',
              })}
            </p>
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="rq-start-sig" className={dialogStyles.label}>
              {t('equipment.requalDashboard.startDialog.sig', {
                defaultValue: 'Signature bundle id',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <input
              id="rq-start-sig"
              className={dialogStyles.input}
              value={sig}
              onChange={(e) => setSig(e.target.value)}
              placeholder="sig-…"
              required
            />
          </div>

          <footer className={dialogStyles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={startM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" variant="primary" isLoading={startM.isPending}>
              {t('equipment.requalDashboard.startDialog.submit', {
                defaultValue: 'Start',
              })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
